import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import VerificationQueue from './VerificationQueue';
import DesignatedInstitutionsTab from './DesignatedInstitutionsTab';
import { designatedInstitutionsApi } from '../../lib/designated-institutions';

type TabKey = 'queue' | 'data';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'queue', label: '인증 심사 큐' },
  { key: 'data', label: '공시 데이터' },
];

const ALL_AGENCY_TYPES = ['CONSTRUCTION_GUIDANCE', 'SAFETY_MANAGEMENT', 'HEALTH_MANAGEMENT'] as const;

export default function Verification() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) ?? 'queue';

  const { data: stats } = useQuery({
    queryKey: ['admin', 'designated-institutions', 'stats'],
    queryFn: () => designatedInstitutionsApi.getStats(),
    staleTime: 60 * 1000,
  });

  const totalFailureCount = stats
    ? ALL_AGENCY_TYPES.reduce((sum, type) => sum + (stats.categories[type]?.recentFailureCount ?? 0), 0)
    : 0;

  const handleTabChange = (tab: TabKey) => {
    if (tab === 'queue') {
      // 기본값이므로 파라미터 제거
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  return (
    <div>
      {/* 페이지 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">기관 인증</h1>
        <p className="text-sm text-gray-500 mt-1">
          인증 심사 큐 및 고용노동부 공시 지정기관 데이터 관리
        </p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === key
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
              {key === 'data' && totalFailureCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
                  {totalFailureCount > 99 ? '99+' : totalFailureCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'queue' && <VerificationQueue />}
      {activeTab === 'data' && <DesignatedInstitutionsTab />}
    </div>
  );
}
